import { emptyListing, SellingValues } from "./types";

export interface LocalDraft {
  version: 1;
  accountId: string;
  savedAt: string;
  step: number;
  values: SellingValues;
  photoNames: string[];
}

export interface DraftStore {
  load(accountId: string): LocalDraft | null;
  save(draft: LocalDraft): void;
  discard(accountId: string): void;
}

/** Future remote drafts require a verified session on the server. Do not send
 * browser account IDs as proof of identity, or serialize LocalPhoto blobs here. */
export interface ServerDraftStore {
  load(signal?: AbortSignal): Promise<LocalDraft | null>;
  save(
    draft: Pick<LocalDraft, "values" | "step">,
    signal?: AbortSignal,
  ): Promise<void>;
  discard(signal?: AbortSignal): Promise<void>;
}

// Account + project + origin scoped. Text only; no credentials, blobs or remote calls.
const key = (accountId: string) =>
  `fp:selling-draft:v1:${process.env.REACT_APP_FIREBASE_PROJECT_ID}:${encodeURIComponent(accountId)}`;
export const localDraftStore: DraftStore = {
  load(accountId) {
    const raw = localStorage.getItem(key(accountId));
    if (!raw) return null;
    const draft: unknown = JSON.parse(raw);
    if (!draft || typeof draft !== "object") throw new Error("Invalid draft");
    const candidate = draft as Partial<LocalDraft>;
    if (
      candidate.version !== 1 ||
      candidate.accountId !== accountId ||
      !Number.isInteger(candidate.step) ||
      candidate.step! < 0 ||
      candidate.step! > 4 ||
      typeof candidate.savedAt !== "string" ||
      !candidate.values ||
      !Object.keys(emptyListing).every(
        (field) =>
          typeof candidate.values?.[field as keyof SellingValues] === "string",
      ) ||
      !Array.isArray(candidate.photoNames) ||
      candidate.photoNames.length > 3 ||
      !candidate.photoNames.every((name) => typeof name === "string")
    )
      throw new Error("Invalid draft");
    const values = Object.fromEntries(
      Object.keys(emptyListing).map((field) => [
        field,
        candidate.values![field as keyof SellingValues],
      ]),
    ) as unknown as SellingValues;
    // A stored draft is untrusted input and must pass the same form validation.
    if (values.condition === "used") values.quantity = "1";
    return { ...candidate, values } as LocalDraft;
  },
  save(draft) {
    localStorage.setItem(key(draft.accountId), JSON.stringify(draft));
  },
  discard(accountId) {
    localStorage.removeItem(key(accountId));
  },
};
